import React, { useCallback, useEffect } from "react";
// @ts-ignore wait for react router update
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { isAccountCompliantSelector } from "@tvg/sh-utils/sessionUtils";
import {
  Promo,
  RaceProgram,
  RaceProgramRaceVideo,
  RaceVideoFeedback
} from "@tvg/ts-types/Race";
import getOptedInPromos from "@tvg/utils/PromoUtils";
import {
  Button,
  Countries,
  Flag,
  Header as Title,
  Icon
} from "@tvg/design-system";
import { get } from "lodash";
import { NullaryFn } from "@tvg/ts-types/Functional";
import { isTvg5 } from "@tvg/utils/generalUtils";
import {
  getOldPromosURLs,
  getOptedInPromosStore
} from "../../../../store/selectors";
import {
  Container,
  LeftColumn,
  RightColumn,
  TitleContainer,
  Wrapper
} from "./styled-components";
import FavoriteButton from "../../FavoriteButton";
import {
  getLiveStreamButtonLabels,
  liveStreaming,
  onClickWatchVideo,
  startingSoon,
  watchReplay
} from "../utils";
import { isPromoExternal, getPromoUrl } from "../../../../utils/promos";
import VideoButton from "../../VideoButton";

export interface SubHeaderProps {
  country: Countries;
  title: string;
  isFavorite: boolean;
  onClickFavorite: () => void;
  video?: RaceProgramRaceVideo;
  videoFeedback?: RaceVideoFeedback;
  race: RaceProgram;
  isLogged: boolean;
  onClickFdrVideo: NullaryFn<void>;
  isFavoriteLoading?: boolean;
}

const SubHeader = ({
  country,
  title = "",
  isFavorite,
  onClickFavorite,
  videoFeedback,
  race,
  isLogged = false,
  onClickFdrVideo,
  isFavoriteLoading
}: SubHeaderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();

  const optedInPromos = useSelector(getOptedInPromosStore);
  const oldPromosURLs: string[] = useSelector(getOldPromosURLs);
  const isAccountCompliant: boolean = useSelector(isAccountCompliantSelector);

  const isStarting = startingSoon(videoFeedback);
  const isWatchReplay = watchReplay(videoFeedback);
  const isLiveStreaming = liveStreaming(videoFeedback);

  const liveStreamButtonLabels = getLiveStreamButtonLabels(
    isStarting,
    isWatchReplay,
    isLiveStreaming
  );

  const promos: Promo | null = getOptedInPromos(
    get(race, "promos", []),
    optedInPromos,
    isLogged
  );

  const isExternal = isPromoExternal({ promos, oldPromosURLs });

  const handleClickWatchVideo = useCallback(
    () =>
      onClickWatchVideo(
        dispatch,
        isLogged,
        race,
        videoFeedback,
        isAccountCompliant
      ),
    [race, isWatchReplay, isLogged]
  );

  useEffect(() => {
    if (searchParams.get("video") && !!race) {
      handleClickWatchVideo();
      searchParams.delete("video");
      setSearchParams(searchParams);
    }
  }, [searchParams, race]);

  return (
    <Container data-qa-label="track-sub-header-container">
      <Wrapper hasButton={!!liveStreamButtonLabels.text || !!promos}>
        <LeftColumn>
          <Flag size="xl" country={country} mr="space-4" qaLabel={country} />
          <TitleContainer>
            <Title tag="h1" qaLabel="race-title" color="blue.000">
              {title}
            </Title>
          </TitleContainer>
          <FavoriteButton
            onClickFavorite={onClickFavorite}
            isFavorite={isFavorite}
            isLoading={isFavoriteLoading}
          />
        </LeftColumn>
        <RightColumn>
          {promos && (
            <Button
              onPress={() => {
                if (typeof window !== "undefined") {
                  window.open(getPromoUrl(isExternal, promos, race, false));
                }
              }}
              qaLabel={
                promos.isOptedIn ? "promo-button" : "promo-button-opt-in"
              }
              icon={
                promos.isOptedIn ? (
                  <Icon
                    name="optedIn"
                    size="s"
                    lineColor="green.600"
                    backgroundColor="green.100"
                    qaLabel="button-icon"
                  />
                ) : (
                  "promo"
                )
              }
              variant={promos.isOptedIn ? "secondary_dark" : "marketing"}
            >
              {promos.isOptedIn ? "Opted-In" : "Promo"}
            </Button>
          )}

          {isTvg5() ? (
            liveStreamButtonLabels.text && (
              <Button
                onPress={handleClickWatchVideo}
                qaLabel={liveStreamButtonLabels.qaLabel}
                variant={isWatchReplay ? "secondary_dark" : "primary"}
                icon={isWatchReplay ? "replay" : "video"}
                isDisabled={isStarting}
                ml="space-3"
              >
                {liveStreamButtonLabels.text}
              </Button>
            )
          ) : (
            <VideoButton
              videoFeedback={videoFeedback}
              onClickWatchVideo={onClickFdrVideo}
            />
          )}
        </RightColumn>
      </Wrapper>
    </Container>
  );
};

export default SubHeader;
